/**
 * Render de los campos variables de una Solicitud a RRHH.
 *
 * No hay un bloque por motivo: se pinta lo que declara el catalogo que
 * publica EMSYS, aplicando visible_si / requerido_si. Es el equivalente del
 * script que controlaba la visibilidad en Flokzu, pero manejado por datos.
 */
import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonNote,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTextarea,
} from "@ionic/react";
import { attachOutline, closeCircleOutline } from "ionicons/icons";
import React, { useRef } from "react";

import SolicitudRRHHBuscador, {
  OpcionBuscada,
} from "src/components/SolicitudRRHHBuscador";
import SolicitudRRHHEvaluacion from "src/components/SolicitudRRHHEvaluacion";
import SolicitudRRHHGrilla from "src/components/SolicitudRRHHGrilla";
import {
  Cargo,
  Colaborador,
  CampoCatalogo,
  FilaGrilla,
  MotivoCatalogo,
  PuntoServicio,
  UnidadNegocio,
  ValorCampo,
  ValoresFormulario,
  camposVisibles,
  esRequerido,
} from "src/services/solicitudesRRHH";

interface Props {
  motivo: MotivoCatalogo | undefined;
  valores: ValoresFormulario;
  archivos: Record<string, File | undefined>;
  errores: Record<string, string>;
  /**
   * Puntos de servicio para los campos de tipo PS. Sin termino de busqueda
   * EMSYS devuelve los del fiscal, asi que esta lista ya viene acotada.
   */
  puntosServicio: PuntoServicio[];
  onCambio: (codigo: string, valor: ValorCampo) => void;
  onArchivo: (codigo: string, archivo: File | undefined) => void;
  /** Buscar otros PS cuando el que hace falta no esta en la lista del fiscal. */
  onBuscarPS: (termino: string) => void;
  /** Unidades de negocio, para las celdas UNIDAD_NEGOCIO de los campos TABLA. */
  unidadesNegocio: UnidadNegocio[];
  /** Cargos vigentes del DWH, para los campos de tipo CARGO. */
  cargos: Cargo[];
  /**
   * Colaboradores activos encontrados, para los campos de tipo OPERARIO. Es
   * una lista aparte de la del colaborador de la cabecera: son dos busquedas
   * distintas y mezclarlas confunde.
   */
  colaboradores: Colaborador[];
  /** Buscar colaboradores para los campos de tipo OPERARIO. */
  onBuscarColaborador: (termino: string) => void;
}

const MIME_ACEPTADOS = "image/*,application/pdf,.doc,.docx,.xls,.xlsx";

const CampoArchivo: React.FC<{
  campo: CampoCatalogo;
  archivo: File | undefined;
  onArchivo: (codigo: string, archivo: File | undefined) => void;
}> = ({ campo, archivo, onArchivo }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={MIME_ACEPTADOS}
        style={{ display: "none" }}
        onChange={(e) => {
          const elegido = e.target.files?.[0];
          onArchivo(campo.codigo, elegido);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
      {archivo ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginTop: "0.5rem",
          }}
        >
          <IonNote style={{ flex: 1, wordBreak: "break-all" }}>
            {archivo.name}
          </IonNote>
          <IonButton
            fill="clear"
            size="small"
            color="danger"
            onClick={() => onArchivo(campo.codigo, undefined)}
          >
            <IonIcon icon={closeCircleOutline} slot="icon-only" />
          </IonButton>
        </div>
      ) : (
        <IonButton
          fill="outline"
          expand="block"
          size="small"
          onClick={() => inputRef.current?.click()}
          style={{ marginTop: "0.5rem" }}
        >
          <IonIcon icon={attachOutline} slot="start" />
          Elegir archivo
        </IonButton>
      )}
    </>
  );
};

const SolicitudRRHHCampos: React.FC<Props> = ({
  motivo,
  valores,
  archivos,
  errores,
  puntosServicio,
  onCambio,
  onArchivo,
  onBuscarPS,
  cargos,
  colaboradores,
  onBuscarColaborador,
  unidadesNegocio,
}) => {
  if (!motivo) return null;

  const visibles = camposVisibles(motivo, valores);

  /**
   * De donde saca cada tipo sus opciones. Los tres muestran titulo + detalle
   * y devuelven el id, que es lo que viaja al servidor.
   *
   * Los cargos ya vienen todos, asi que se filtran en el navegador (sin
   * onBuscar); los otros dos buscan contra EMSYS, que es donde estan.
   */
  const buscadorDe = (
    tipo: "PS" | "OPERARIO" | "CARGO",
  ): {
    opciones: OpcionBuscada[];
    placeholder: string;
    onBuscar?: (t: string) => void;
    vacio?: string;
  } => {
    if (tipo === "PS") {
      return {
        opciones: puntosServicio.map((p) => ({
          id: String(p.ps_id),
          titulo: p.nombre,
          detalle: p.codigo || undefined,
        })),
        placeholder: "Buscar punto de servicio",
        onBuscar: onBuscarPS,
      };
    }
    if (tipo === "OPERARIO") {
      return {
        opciones: colaboradores.map((c) => ({
          id: String(c.operario_id),
          titulo: c.nombre,
          detalle: `Legajo ${c.legajo || "-"} · CI ${c.cedula || "-"}`,
        })),
        placeholder: "Buscar por legajo, cédula o nombre",
        onBuscar: onBuscarColaborador,
      };
    }
    return {
      opciones: cargos.map((c) => ({
        id: String(c.cargo_id),
        titulo: c.nombre,
      })),
      placeholder: "Buscar cargo",
      vacio: "No se pudo leer la lista de cargos. Reintentá en un momento.",
    };
  };

  const renderControl = (campo: CampoCatalogo) => {
    const valor = valores[campo.codigo];

    // Los calculados los llena EMSYS: se muestran de solo lectura.
    if (campo.calculado) {
      const texto = valor === null || valor === undefined ? "" : String(valor);
      if (texto.includes("\n")) {
        return <IonTextarea value={texto} rows={3} autoGrow readonly />;
      }
      return <IonInput value={texto} readonly placeholder="Lo calcula EMSYS" />;
    }

    switch (campo.tipo) {
      case "TEXTO_LARGO":
        return (
          <IonTextarea
            value={valor === null || valor === undefined ? "" : String(valor)}
            rows={3}
            autoGrow
            placeholder="Escribi el detalle"
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value || "")}
          />
        );

      case "ENTERO":
        return (
          <IonInput
            type="number"
            min="0"
            step="1"
            value={valor === null || valor === undefined ? "" : String(valor)}
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value || "")}
          />
        );

      case "DECIMAL":
        return (
          <IonInput
            type="text"
            inputmode="decimal"
            placeholder="Ej: 1.500.000"
            value={valor === null || valor === undefined ? "" : String(valor)}
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value || "")}
          />
        );

      case "FECHA":
        // input date nativo: el date picker de Ionic en formularios largos es
        // mas incomodo que el del sistema, y EMSYS acepta YYYY-MM-DD.
        return (
          <IonInput
            type="date"
            value={valor === null || valor === undefined ? "" : String(valor)}
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value || "")}
          />
        );

      case "BOOL":
        return (
          <IonSelect
            value={
              valor === null || valor === undefined || valor === ""
                ? ""
                : String(valor)
            }
            placeholder="Elegi una opcion"
            interface="action-sheet"
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value)}
          >
            <IonSelectOption value="true">Si</IonSelectOption>
            <IonSelectOption value="false">No</IonSelectOption>
          </IonSelect>
        );

      case "OPCION":
        return (
          <IonSelect
            value={valor === null || valor === undefined ? "" : String(valor)}
            placeholder="Elegi una opcion"
            interface="action-sheet"
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value)}
          >
            {campo.opciones.map((o) => (
              <IonSelectOption key={o.valor} value={o.valor}>
                {o.texto}
              </IonSelectOption>
            ))}
          </IonSelect>
        );

      case "ARCHIVO":
        return (
          <CampoArchivo
            campo={campo}
            archivo={archivos[campo.codigo]}
            onArchivo={onArchivo}
          />
        );

      default:
        return (
          <IonInput
            type="text"
            value={valor === null || valor === undefined ? "" : String(valor)}
            onIonChange={(e) => onCambio(campo.codigo, e.detail.value || "")}
          />
        );
    }
  };

  return (
    <>
      {visibles.map((campo) => {
        const obligatorio = !campo.calculado && esRequerido(campo, valores);
        const error = errores[campo.codigo];

        // Los que viajan por id (punto de servicio, colaborador, cargo)
        // tampoco entran en un IonItem: son un buscador con su lista de
        // resultados, igual que el buscador de colaborador de la cabecera.
        if (campo.tipo === "PS" || campo.tipo === "OPERARIO" || campo.tipo === "CARGO") {
          const cfg = buscadorDe(campo.tipo);
          return (
            <div key={campo.codigo} style={{ marginBottom: 8 }}>
              <IonItem lines="none">
                <IonLabel color={error ? "danger" : undefined}>
                  <h2 style={{ fontWeight: 600 }}>
                    {campo.etiqueta}
                    {obligatorio ? " *" : ""}
                  </h2>
                  {(error || campo.ayuda) && (
                    <IonNote color={error ? "danger" : "medium"}>
                      {error || campo.ayuda}
                    </IonNote>
                  )}
                </IonLabel>
              </IonItem>
              <SolicitudRRHHBuscador
                opciones={cfg.opciones}
                valor={
                  valores[campo.codigo] === null ||
                  valores[campo.codigo] === undefined
                    ? ""
                    : String(valores[campo.codigo])
                }
                onElegir={(id) => onCambio(campo.codigo, id)}
                placeholder={cfg.placeholder}
                onBuscar={cfg.onBuscar}
                vacio={cfg.vacio}
              />
            </div>
          );
        }

        // Los campos TABLA no entran en un IonItem con label apilado: son una
        // lista de tarjetas con su propio boton de agregar, asi que van al
        // ancho completo (ver SolicitudRRHHGrilla).
        if (campo.tipo === "TABLA") {
          const filas = Array.isArray(valores[campo.codigo])
            ? (valores[campo.codigo] as FilaGrilla[])
            : [];
          return (
            <div key={campo.codigo} style={{ marginBottom: 8 }}>
              <IonItem lines="none">
                <IonLabel color={error ? "danger" : undefined}>
                  <h2 style={{ fontWeight: 600 }}>
                    {campo.etiqueta}
                    {obligatorio ? " *" : ""}
                  </h2>
                  {(error || campo.ayuda) && (
                    <IonNote color={error ? "danger" : "medium"}>
                      {error || campo.ayuda}
                    </IonNote>
                  )}
                </IonLabel>
              </IonItem>
              <SolicitudRRHHGrilla
                campo={campo}
                filas={filas}
                unidades={unidadesNegocio}
                onCambio={(nuevas) => onCambio(campo.codigo, nuevas)}
              />
            </div>
          );
        }

        // La evaluacion de desempeño tampoco: son 17 conceptos con sus
        // respuestas, al ancho completo (ver SolicitudRRHHEvaluacion).
        if (campo.tipo === "EVALUACION") {
          return (
            <div key={campo.codigo} style={{ marginBottom: 8 }}>
              <IonItem lines="none">
                <IonLabel color={error ? "danger" : undefined}>
                  <h2 style={{ fontWeight: 600 }}>
                    {campo.etiqueta}
                    {obligatorio ? " *" : ""}
                  </h2>
                  {campo.ayuda && <IonNote color="medium">{campo.ayuda}</IonNote>}
                </IonLabel>
              </IonItem>
              <SolicitudRRHHEvaluacion
                campo={campo}
                valor={valores[campo.codigo]}
                requerido={obligatorio}
                error={error}
                onCambio={(nuevo) => onCambio(campo.codigo, nuevo)}
              />
            </div>
          );
        }

        return (
          <IonItem key={campo.codigo} lines="full">
            <IonLabel position="stacked" color={error ? "danger" : undefined}>
              {campo.etiqueta}
              {obligatorio ? " *" : ""}
            </IonLabel>
            {renderControl(campo)}
            {error && (
              <IonNote slot="helper" color="danger">
                {error}
              </IonNote>
            )}
            {!error && campo.ayuda && (
              <IonNote slot="helper" color="medium">
                {campo.ayuda}
              </IonNote>
            )}
          </IonItem>
        );
      })}
    </>
  );
};

export default SolicitudRRHHCampos;
